import React, { useMemo } from 'react';
import { BsArrowLeft } from 'react-icons/bs';
import { MdChecklistRtl, MdOutlineQuestionAnswer } from 'react-icons/md';
import { BiBarcodeReader } from 'react-icons/bi';
import dayjs from 'dayjs';

import Modal, { ModalType } from 'legacy/common/components/modal/Modal';
import FadeIn from 'legacy/common/components/transition/FadeIn';
import { useGetEventEntryGuestDetail } from '../../../hooks';
import Loader from 'legacy/common/components/layout/Loader';
import TicketDetails from './components/TicketDetails';
import DetailsBlock from './components/DetailsBlock';
import EntryLog from './components/EntryLog';
import GuestHeader from './components/GuestHeader';
import { GuestEntryTier, GuestEntryUser } from 'legacy/tickets/types';

interface GuestDetailsModalProps {
  eventId: string;
  selectedTicket: string;
  onClose: () => void;
  isOrganizer: boolean;
  isCloseEnabled?: boolean;
  onRefundTicket: (params: {
    ticketId: string;
    tierData: GuestEntryTier;
    purchaser: GuestEntryUser;
  }) => void;
}

const GuestDetailsModal: React.FC<GuestDetailsModalProps> = ({
  onClose,
  selectedTicket,
  eventId,
  onRefundTicket,
  isOrganizer,
  isCloseEnabled = false
}) => {
  const { data, isLoading } = useGetEventEntryGuestDetail({
    eventId,
    ticketId: selectedTicket
  });

  const entryLogData = useMemo(
    () =>
      data?.entryLog?.map((entry) => ({
        label: entry?.type,
        key: entry.date.toString(),
        value: `${dayjs(data?.createdAt).format('MMM D, YYYY | h:mma')} By - ${
          entry?.user?.firstName
        } ${entry?.user?.lastName}`
      })) || [],
    [data?.entryLog]
  );

  const screeningQuestionsData = useMemo(
    () =>
      data?.screeningQuestions?.map((answer) => ({
        label: answer.question,
        key: answer.questionId,
        value: answer.answer
      })) || [],
    [data?.screeningQuestions]
  );

  const guestQuestionsData = useMemo(
    () =>
      data?.guestQuestions?.map((answer) => ({
        label: answer.question,
        key: answer.questionId,
        value: answer.answer
      })) || [],
    [data?.guestQuestions]
  );

  return (
    <Modal
      visible
      type={ModalType.SMALL}
      onClose={isCloseEnabled ? onClose : null}
      hideCloseButton
    >
      <Loader isOverlay isLoading={isLoading}>
        {data && (
          <div className="relative overflow-y-auto">
            {!isCloseEnabled && (
              <div
                className="absolute top-4 left-2 h-7 w-7 bg-gray-100 z-30 rounded-full flex items-center justify-center cursor-pointer"
                onClick={onClose}
              >
                <BsArrowLeft />
              </div>
            )}
            <FadeIn>
              <GuestHeader data={data} />
              <TicketDetails
                data={data}
                onRefundTicket={onRefundTicket}
                isOrganizer={isOrganizer}
              />
              <EntryLog
                data={entryLogData}
                title="Entry log"
                Icon={BiBarcodeReader}
              />
              <DetailsBlock
                data={screeningQuestionsData}
                title="Screening questions"
                Icon={MdChecklistRtl}
              />
              <DetailsBlock
                data={guestQuestionsData}
                title="Guest questions"
                Icon={MdOutlineQuestionAnswer}
              />
            </FadeIn>
          </div>
        )}
      </Loader>
    </Modal>
  );
};

export default GuestDetailsModal;
